/**
 * money.js — para formatlama CEKIRDEGI (Muhasebe Faz 3.7a tekillestirme).
 *
 * Once bu dosyada ve utils/formatCurrency.js'de birbirinden bagimsiz iki
 * implementasyon vardi (fmtMoney / fmtTRY), farkli cikti bicimleri
 * uretiyorlardi. Bu turda ikisi de asagidaki CEKIRDEK fonksiyonlarin ince
 * sarmalayicisi haline getirildi — DAVRANISLARI (input->output eslesmesi)
 * BIREBIR KORUNDU, sadece kod tekrari kaldirildi. Kanit:
 * __tests__/utils/money.test.js "parity" bloklari.
 *
 * Cagiran kod acisindan hicbir sey degismedi: `fmtMoney`, `fmtPercent`
 * (bu dosyadan) ve `fmtTRY` (utils/formatCurrency.js) eskisi gibi calisir.
 */

/** fmtTRY'nin parseFloat semantigi: string/null/undefined/NaN -> 0. */
function toNumberStrict(val) {
  return Number.parseFloat(val) || 0;
}

/** fmtMoney/fmtPercent'in Number(n || 0) semantigi. */
function toNumberLoose(val) {
  return Number(val || 0);
}

/**
 * formatCurrencyIntl — Intl.NumberFormat(style:'currency') tabanli cekirdek.
 * `fmtTRY`'nin ta kendisi; sarmalayici sadece varsayilan parametreleri sabitler.
 */
export function formatCurrencyIntl(val, {
  locale = 'tr-TR',
  currency = 'TRY',
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
} = {}) {
  const num = toNumberStrict(val);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

/**
 * formatMoneyAuto — `fmtMoney`'in ta kendisi: tam sayi tutarlar kurussuz,
 * kuruslu tutarlar 2 ondalik basamakla gosterilir, sembol BASTA.
 */
export function formatMoneyAuto(val, { locale = 'tr-TR', symbol = '₺' } = {}) {
  const num = toNumberLoose(val);
  const hasCents = Math.abs(num - Math.round(num)) > 0.001;
  const formatted = num.toLocaleString(locale, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
  return `${symbol}${formatted}`;
}

/**
 * formatMoneyFixed2 — muhasebe ekranlari icin YENI varyant: her zaman 2
 * ondalik basamak, sembol BASTA, null/undefined/0 -> "symbol0,00"
 * ("veri yok" degil, "sifir tutar" — plan §11.3).
 */
export function formatMoneyFixed2(val, { locale = 'tr-TR', symbol = '₺' } = {}) {
  const num = toNumberLoose(val);
  const formatted = num.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}

/**
 * fmtMoney(1234.5)  -> "₺1.234,50"
 * fmtMoney(24000)   -> "₺24.000"
 * fmtMoney(5333.33) -> "₺5.333,33"
 * fmtMoney(0)       -> "₺0"
 * fmtMoney(null)    -> "₺0"
 */
export function fmtMoney(n) {
  return formatMoneyAuto(n, { locale: 'tr-TR', symbol: '₺' });
}

/**
 * fmtMoneyAccounting — Muhasebe alt sekmeleri (Faz 3.6+, AccountingTab) icin
 * dil duyarli, sabit 2 ondalik basamakli varyant. tabular-nums ile hizalamaya
 * uygun sabit genislikli cikti uretir; bos/sifir tutar HER ZAMAN "₺0,00"
 * ("—" DEGIL — plan §11.3, muhasebede "veri yok" != "sifir tutar").
 *
 *   fmtMoneyAccounting(1234.5, 'tr')  -> "₺1.234,50"
 *   fmtMoneyAccounting(1234.5, 'en')  -> "₺1,234.50"
 *   fmtMoneyAccounting(0, 'tr')       -> "₺0,00"
 *   fmtMoneyAccounting(null, 'tr')    -> "₺0,00"
 *   fmtMoneyAccounting(undefined, 'en') -> "₺0.00"
 */
export function fmtMoneyAccounting(n, lang = 'tr') {
  return formatMoneyFixed2(n, { locale: lang === 'en' ? 'en-US' : 'tr-TR', symbol: '₺' });
}

/**
 * fmtPercent(n, lang) — oran yüzdesini dile göre yerelleştirilmiş ondalık
 * ayraçla (TR: virgül, EN: nokta) 2 basamak sabit hassasiyetle biçimlendirir.
 *
 * `null`/`undefined`/`NaN` "—" döner — "veri yok" ile "oran gerçekten 0"
 * ayrımı bu fonksiyonun kendi sözleşmesinde korunur, çağıranın elle kontrol
 * etmesine bırakılmaz (backend Faz 5, sıfıra bölmede bilinçli `null` döner).
 * `0` geçerli bir değerdir, "%0,00" olarak biçimlenmeye devam eder.
 *
 *   fmtPercent(54.6, 'tr')  -> "%54,60"
 *   fmtPercent(54.6, 'en')  -> "%54.60"
 *   fmtPercent(35.17, 'tr') -> "%35,17"
 *   fmtPercent(0, 'tr')     -> "%0,00"
 *   fmtPercent(null, 'tr')  -> "—"
 */
export function fmtPercent(n, lang) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  const formatted = new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
  return `%${formatted}`;
}
